import {
  App,
  Button,
  Card,
  DatePicker,
  Drawer,
  Empty,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  DeleteOutlined,
  DollarOutlined,
  PlusOutlined,
  RollbackOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  inventoryApi,
  type StoreSale,
  type StoreSaleInput,
  type StoreCustomerType,
  type StoreSalePayment,
  type StoreItem,
} from '@/api/inventory';
import { financeApi } from '@/api/finance';
import { studentsApi } from '@/api/students';
import { useAuthStore } from '@/store/auth';
import { extractError } from '@/shared/lib/error-message';
import { useReasonPrompt } from '@/shared/hooks/use-reason-prompt';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

interface PosLine {
  storeItemId: number;
  itemName: string;
  unitPrice: number;
  quantity: number;
}

export function StoreSalesTab() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('INVENTORY:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('INVENTORY:EDIT'));
  const askReason = useReasonPrompt();

  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [posOpen, setPosOpen] = useState(false);
  const [viewing, setViewing] = useState<StoreSale | null>(null);

  const params = { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') };
  const { data, isFetching } = useQuery({
    queryKey: ['inventory', 'store', 'sales', params],
    queryFn: () => inventoryApi.listStoreSales(params.from, params.to),
  });

  const reverse = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      inventoryApi.reverseStoreSale(id, reason),
    onSuccess: () => {
      message.success('Sale reversed');
      qc.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const total = (data ?? []).filter((s) => !s.reversed).reduce((sum, s) => sum + Number(s.netAmount), 0);

  const columns: TableProps<StoreSale>['columns'] = [
    {
      title: 'Receipt',
      dataIndex: 'receiptNumber',
      width: 100,
      render: (n: number) => (
        <Tag color="processing" style={{ fontFamily: 'monospace' }}>#{n}</Tag>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'saleDate',
      width: 110,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Customer',
      render: (_, row) =>
        row.customerType === 'STUDENT' ? (
          <Space orientation="vertical" size={0}>
            <Typography.Text strong delete={row.reversed}>{row.studentName}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.studentRegistrationNumber}
            </Typography.Text>
          </Space>
        ) : (
          <Space orientation="vertical" size={0}>
            <Typography.Text strong delete={row.reversed}>{row.walkInName}</Typography.Text>
            <Tag>Walk-in</Tag>
          </Space>
        ),
    },
    {
      title: 'Net',
      dataIndex: 'netAmount',
      width: 120,
      align: 'right',
      render: (a: string, row) => (
        <Typography.Text strong delete={row.reversed}>PKR {PKR(a)}</Typography.Text>
      ),
    },
    {
      title: 'Outstanding',
      dataIndex: 'outstanding',
      width: 130,
      align: 'right',
      render: (v: string) =>
        Number(v) > 0 ? (
          <Tag color="warning" style={{ fontWeight: 600 }}>PKR {PKR(v)}</Tag>
        ) : (
          <Tag color="success">Paid</Tag>
        ),
    },
    {
      title: 'Payment',
      dataIndex: 'paymentType',
      width: 100,
      render: (p: StoreSalePayment) => <Tag color={p === 'CASH' ? 'success' : 'warning'}>{p}</Tag>,
    },
    {
      title: '',
      width: 200,
      align: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" type="text" onClick={() => setViewing(row)}>
            Open
          </Button>
          {canEdit && !row.reversed && (
            <Button
              size="small"
              type="text"
              icon={<RollbackOutlined />}
              onClick={async () => {
                const reason = await askReason({
                  title: `Reverse sale #${row.receiptNumber}?`,
                  placeholder: 'Why is this sale being reversed?',
                  okText: 'Reverse sale',
                  danger: true,
                });
                if (reason) reverse.mutate({ id: row.id, reason });
              }}
            >
              Reverse
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
        {data && data.length > 0 && (
          <Tag color="processing" style={{ fontSize: 13 }}>
            Net · PKR {PKR(total)}
          </Tag>
        )}
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setPosOpen(true)}>
            New sale
          </Button>
        )}
      </Space>

      {data && data.length === 0 && !isFetching ? (
        <Empty description="No sales in this range" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            size="middle"
            loading={isFetching}
            columns={columns}
            dataSource={data ?? []}
            pagination={{ pageSize: 25 }}
          />
        </Card>
      )}

      {posOpen && <PosModal onClose={() => setPosOpen(false)} />}
      {viewing && (
        <SaleDetailDrawer sale={viewing} onClose={() => setViewing(null)} canCreate={canCreate} />
      )}
    </div>
  );
}

function PosModal({ onClose }: { onClose: () => void }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [customerType, setCustomerType] = useState<StoreCustomerType>('STUDENT');
  const [enrollmentId, setEnrollmentId] = useState<number | undefined>(undefined);
  const [studentLabel, setStudentLabel] = useState<string>('');
  const [walkInName, setWalkInName] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [paymentType, setPaymentType] = useState<StoreSalePayment>('CASH');
  const [bankAccountId, setBankAccountId] = useState<number | undefined>(undefined);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [paidNow, setPaidNow] = useState<number>(0);
  const [saleDate, setSaleDate] = useState<Dayjs>(dayjs());
  const [lines, setLines] = useState<PosLine[]>([]);

  const { data: items } = useQuery({
    queryKey: ['inventory', 'store', 'items'],
    queryFn: inventoryApi.listStoreItems,
    staleTime: 60_000,
  });

  const { data: bankAccounts } = useQuery({
    queryKey: ['finance', 'bank-accounts'],
    queryFn: financeApi.listBankAccounts,
    staleTime: 60_000,
  });

  const { data: studentResults, isFetching: searching } = useQuery({
    queryKey: ['students', 'search', studentSearch],
    queryFn: () => studentsApi.search({ q: studentSearch.trim() || undefined, size: 15 }),
    enabled: studentSearch.trim().length > 0,
  });

  const submit = useMutation({
    mutationFn: () => {
      const body: StoreSaleInput = {
        saleDate: saleDate.format('YYYY-MM-DD'),
        customerType,
        enrollmentId: customerType === 'STUDENT' ? enrollmentId : null,
        walkInName: customerType === 'WALK_IN' ? walkInName.trim() : undefined,
        lines: lines.map((l) => ({
          storeItemId: l.storeItemId,
          quantity: l.quantity.toFixed(2),
          unitPriceOverride: l.unitPrice.toFixed(2),
        })),
        discountAmount: Number(discountAmount).toFixed(2),
        paymentType,
        bankAccountId: paymentType === 'CASH' || (paidNow ?? 0) > 0 ? bankAccountId : null,
        paidNow:
          paymentType === 'CASH'
            ? net.toFixed(2)
            : Number(paidNow ?? 0).toFixed(2),
      };
      return inventoryApi.addStoreSale(body);
    },
    onSuccess: (s) => {
      message.success(`Sale recorded · receipt #${s.receiptNumber}`);
      qc.invalidateQueries({ queryKey: ['inventory'] });
      onClose();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const subtotal = lines.reduce((s, l) => s + l.unitPrice * l.quantity, 0);
  const net = Math.max(0, subtotal - (Number.isFinite(discountAmount) ? discountAmount : 0));

  const addLine = (itemId: number) => {
    const item = items?.find((i) => i.id === itemId);
    if (!item) return;
    if (lines.some((l) => l.storeItemId === itemId)) {
      setLines((cur) =>
        cur.map((l) =>
          l.storeItemId === itemId ? { ...l, quantity: l.quantity + 1 } : l,
        ),
      );
      return;
    }
    setLines((cur) => [
      ...cur,
      {
        storeItemId: itemId,
        itemName: item.name,
        unitPrice: Number(item.sellingPrice),
        quantity: 1,
      },
    ]);
  };

  const updateLine = (itemId: number, patch: Partial<PosLine>) => {
    setLines((cur) => cur.map((l) => (l.storeItemId === itemId ? { ...l, ...patch } : l)));
  };

  const removeLine = (itemId: number) => {
    setLines((cur) => cur.filter((l) => l.storeItemId !== itemId));
  };

  const studentOptions =
    studentResults?.content.map((s) => ({
      value: s.id,
      label: `${s.firstName} ${s.lastName} · ${s.registrationNumber}`,
    })) ?? [];

  const canSubmit =
    lines.length > 0 &&
    net > 0 &&
    (customerType === 'STUDENT'
      ? enrollmentId != null
      : walkInName.trim().length > 0) &&
    (paymentType === 'CASH' ? bankAccountId != null : (paidNow > 0 ? bankAccountId != null : true));

  return (
    <Modal
      title="New store sale"
      open
      onCancel={onClose}
      onOk={() => submit.mutate()}
      okText="Record sale"
      okButtonProps={{
        disabled: !canSubmit,
        loading: submit.isPending,
        icon: <ShoppingCartOutlined />,
      }}
      destroyOnHidden
      width={720}
    >
      <Space orientation="vertical" style={{ width: '100%' }} size={12}>
        <Space wrap>
          <Select
            value={customerType}
            onChange={(v) => setCustomerType(v)}
            options={[
              { value: 'STUDENT', label: 'Student' },
              { value: 'WALK_IN', label: 'Walk-in' },
            ]}
            style={{ width: 130 }}
          />
          {customerType === 'STUDENT' ? (
            <Select
              showSearch
              placeholder="Search student"
              loading={searching}
              filterOption={false}
              onSearch={(v) => setStudentSearch(v)}
              notFoundContent={studentSearch.trim() === '' ? 'Type to search…' : 'No matches'}
              options={studentOptions}
              value={enrollmentId != null ? studentLabel : undefined}
              onChange={async (val: number) => {
                const detail = await studentsApi.get(val);
                if (!detail.currentEnrollment) {
                  message.error('Student has no active enrollment — enroll them first');
                  return;
                }
                setEnrollmentId(detail.currentEnrollment.id);
                setStudentLabel(`${detail.firstName} ${detail.lastName} · ${detail.registrationNumber}`);
              }}
              labelInValue={false}
              style={{ minWidth: 320 }}
            />
          ) : (
            <Input
              placeholder="Walk-in customer name"
              value={walkInName}
              onChange={(e) => setWalkInName(e.target.value)}
              style={{ width: 320 }}
            />
          )}
          <DatePicker
            value={saleDate}
            onChange={(d) => d && setSaleDate(d)}
            format="DD MMM YYYY"
            allowClear={false}
          />
        </Space>

        <Select
          showSearch
          placeholder="+ Add item"
          optionFilterProp="label"
          style={{ width: '100%' }}
          value={undefined}
          onChange={(v) => addLine(v)}
          options={(items ?? [])
            .filter((i) => Number(i.currentStock) > 0)
            .map((i: StoreItem) => ({
              value: i.id,
              label: `${i.name} · stock ${PKR(i.currentStock)} · PKR ${PKR(i.sellingPrice)}`,
            }))}
        />

        {lines.length === 0 ? (
          <Empty description="Add items to start the sale" />
        ) : (
          <Card styles={{ body: { padding: 0 } }}>
            <Table
              rowKey="storeItemId"
              size="small"
              dataSource={lines}
              pagination={false}
              columns={[
                { title: 'Item', dataIndex: 'itemName' },
                {
                  title: 'Qty',
                  width: 100,
                  render: (_, row) => (
                    <InputNumber
                      size="small"
                      min={0.01}
                      step={1}
                      value={row.quantity}
                      onChange={(v) => v != null && updateLine(row.storeItemId, { quantity: Number(v) })}
                    />
                  ),
                },
                {
                  title: 'Price',
                  width: 120,
                  render: (_, row) => (
                    <InputNumber
                      size="small"
                      min={0}
                      step={50}
                      value={row.unitPrice}
                      onChange={(v) => v != null && updateLine(row.storeItemId, { unitPrice: Number(v) })}
                    />
                  ),
                },
                {
                  title: 'Total',
                  width: 110,
                  align: 'right',
                  render: (_, row) => `PKR ${PKR(row.unitPrice * row.quantity)}`,
                },
                {
                  title: '',
                  width: 50,
                  render: (_, row) => (
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeLine(row.storeItemId)}
                    />
                  ),
                },
              ]}
            />
          </Card>
        )}

        <Space wrap>
          <span>Subtotal: <b>PKR {PKR(subtotal)}</b></span>
          <span>−</span>
          <span>Discount:</span>
          <InputNumber
            size="small"
            min={0}
            max={subtotal}
            value={discountAmount}
            onChange={(v) => setDiscountAmount(Number(v ?? 0))}
          />
          <span>=</span>
          <Tag color="processing" style={{ fontSize: 13 }}>
            Net · PKR {PKR(net)}
          </Tag>
        </Space>

        <Space wrap>
          <Select
            value={paymentType}
            onChange={(v) => {
              setPaymentType(v);
              if (v === 'CASH') setPaidNow(net);
            }}
            options={
              customerType === 'WALK_IN'
                ? [{ value: 'CASH', label: 'Cash' }]
                : [
                    { value: 'CASH', label: 'Cash (full)' },
                    { value: 'CREDIT', label: 'Credit (defer)' },
                  ]
            }
            style={{ width: 160 }}
          />
          {paymentType === 'CREDIT' && (
            <>
              <span>Paid now:</span>
              <InputNumber
                size="small"
                min={0}
                max={net}
                value={paidNow}
                onChange={(v) => setPaidNow(Number(v ?? 0))}
              />
            </>
          )}
          {(paymentType === 'CASH' || paidNow > 0) && (
            <Select
              placeholder="Receive into"
              value={bankAccountId}
              onChange={(v) => setBankAccountId(v)}
              showSearch
              optionFilterProp="label"
              options={(bankAccounts ?? [])
                .filter((b) => (paymentType === 'CASH' ? b.cash : true))
                .map((b) => ({
                  value: b.id,
                  label: b.accountName + (b.bankName ? ` · ${b.bankName}` : ''),
                }))}
              style={{ minWidth: 220 }}
            />
          )}
        </Space>
      </Space>
    </Modal>
  );
}

function SaleDetailDrawer({
  sale,
  onClose,
  canCreate,
}: {
  sale: StoreSale;
  onClose: () => void;
  canCreate: boolean;
}) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [paying, setPaying] = useState(false);
  const [payAmount, setPayAmount] = useState<number>(Number(sale.outstanding));
  const [payAccount, setPayAccount] = useState<number | undefined>(undefined);
  const [payDate, setPayDate] = useState<Dayjs>(dayjs());

  const { data: payments } = useQuery({
    queryKey: ['inventory', 'store', 'sales', sale.id, 'credit-payments'],
    queryFn: () => inventoryApi.listCreditPayments(sale.id),
    enabled: sale.paymentType === 'CREDIT',
  });

  const { data: bankAccounts } = useQuery({
    queryKey: ['finance', 'bank-accounts'],
    queryFn: financeApi.listBankAccounts,
    staleTime: 60_000,
  });

  const pay = useMutation({
    mutationFn: () =>
      inventoryApi.payCredit({
        saleId: sale.id,
        paymentDate: payDate.format('YYYY-MM-DD'),
        amount: Number(payAmount).toFixed(2),
        bankAccountId: payAccount!,
      }),
    onSuccess: () => {
      message.success('Payment recorded');
      qc.invalidateQueries({ queryKey: ['inventory'] });
      setPaying(false);
      onClose();
    },
    onError: (e) => message.error(extractError(e)),
  });

  return (
    <Drawer open onClose={onClose} title={`Receipt #${sale.receiptNumber}`} size={520}>
      <Space orientation="vertical" style={{ width: '100%' }} size={12}>
        <Typography.Text>
          {sale.customerType === 'STUDENT'
            ? `${sale.studentName} · ${sale.studentRegistrationNumber}`
            : `${sale.walkInName} · walk-in`}
        </Typography.Text>
        <Typography.Text type="secondary">{dayjs(sale.saleDate).format('D MMM YYYY')}</Typography.Text>

        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            size="small"
            dataSource={sale.lines}
            pagination={false}
            columns={[
              { title: 'Item', dataIndex: 'storeItemName' },
              { title: 'Qty', dataIndex: 'quantity', width: 70, align: 'right' },
              {
                title: 'Price',
                dataIndex: 'unitPrice',
                width: 100,
                align: 'right',
                render: (v: string) => `PKR ${PKR(v)}`,
              },
              {
                title: 'Total',
                dataIndex: 'lineTotal',
                width: 110,
                align: 'right',
                render: (v: string) => `PKR ${PKR(v)}`,
              },
            ]}
          />
        </Card>

        <Space orientation="vertical" size={2}>
          <Typography.Text>Total: PKR {PKR(sale.totalAmount)}</Typography.Text>
          {Number(sale.discountAmount) > 0 && (
            <Typography.Text type="secondary">Discount: PKR {PKR(sale.discountAmount)}</Typography.Text>
          )}
          <Typography.Text strong>Net: PKR {PKR(sale.netAmount)}</Typography.Text>
          <Typography.Text>Paid: PKR {PKR(sale.paidAmount)}</Typography.Text>
          {Number(sale.outstanding) > 0 && (
            <Tag color="warning">Outstanding · PKR {PKR(sale.outstanding)}</Tag>
          )}
          {sale.reversed && <Tag color="default">Reversed · {sale.reversalReason}</Tag>}
        </Space>

        {sale.paymentType === 'CREDIT' && payments && payments.length > 0 && (
          <>
            <Typography.Text strong>Credit collections</Typography.Text>
            <Table
              rowKey="id"
              size="small"
              dataSource={payments}
              pagination={false}
              columns={[
                {
                  title: 'Date',
                  dataIndex: 'paymentDate',
                  render: (d: string) => dayjs(d).format('D MMM YYYY'),
                },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  align: 'right',
                  render: (a: string) => `PKR ${PKR(a)}`,
                },
                { title: 'Account', dataIndex: 'bankAccountName' },
              ]}
            />
          </>
        )}

        {canCreate && Number(sale.outstanding) > 0 && !sale.reversed && (
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={() => {
              setPaying(true);
              setPayAmount(Number(sale.outstanding));
            }}
          >
            Collect payment
          </Button>
        )}
      </Space>

      <Modal
        title={`Collect against #${sale.receiptNumber}`}
        open={paying}
        onCancel={() => setPaying(false)}
        onOk={() => pay.mutate()}
        confirmLoading={pay.isPending}
        okText="Record"
        okButtonProps={{ disabled: !payAccount || payAmount <= 0 }}
        destroyOnHidden
      >
        <Space orientation="vertical" style={{ width: '100%' }} size={12}>
          <DatePicker
            value={payDate}
            onChange={(d) => d && setPayDate(d)}
            format="DD MMM YYYY"
            allowClear={false}
            style={{ width: '100%' }}
          />
          <InputNumber
            style={{ width: '100%' }}
            min={0.01}
            max={Number(sale.outstanding)}
            value={payAmount}
            onChange={(v) => setPayAmount(Number(v ?? 0))}
            addonBefore="PKR"
          />
          <Select
            style={{ width: '100%' }}
            placeholder="Receive into"
            value={payAccount}
            onChange={(v) => setPayAccount(v)}
            options={(bankAccounts ?? []).map((b) => ({
              value: b.id,
              label: b.accountName + (b.bankName ? ` · ${b.bankName}` : ''),
            }))}
          />
        </Space>
      </Modal>
    </Drawer>
  );
}
